import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRecipe } from '../../contexts/RecipeContext';
import { useAuth } from '../../contexts/AuthContext';
import { FaShoppingCart, FaUser, FaListUl, FaClock, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import '../../styles/recipes/JoinShareHandler.css';

/**
 * JoinShareHandler - Verarbeitet Share-Links für Einkaufszettel
 *
 * Props:
 * - token: Share token from URL
 * - onClose: Callback to close the handler
 * - onSuccess: Callback after successful join
 *
 * Features:
 * - Zeigt Share-Info an (Listenname, Owner, Anzahl Items)
 * - Ermöglicht Beitritt zur Liste
 * - Weiterleitung nach erfolgreichem Beitritt
 * - Fehlerbehandlung für ungültige/abgelaufene Links
 */
const JoinShareHandler = ({ token, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { getShoppingListShareInfo, joinSharedList } = useRecipe();

  const [shareInfo, setShareInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [joinResult, setJoinResult] = useState(null);
  const [error, setError] = useState(null);

  // Load share info on mount
  useEffect(() => {
    const loadShareInfo = async () => {
      if (!token) {
        setError(t('share.invalidLink') || 'Ungültiger Share-Link');
        setIsLoading(false);
        return;
      }

      try {
        const info = await getShoppingListShareInfo(token);
        setShareInfo(info);
        if (!info.valid) {
          setError(info.message || t('share.invalidLink') || 'Ungültiger Share-Link');
        }
      } catch (err) {
        setError(t('share.loadError') || 'Fehler beim Laden der Share-Informationen');
        console.error('Error loading share info:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadShareInfo();
  }, [token, getShoppingListShareInfo, t]);

  // Handle join
  const handleJoin = async () => {
    if (!user) {
      // Store return URL and trigger login
      sessionStorage.setItem('shareReturnToken', token);
      onClose?.();
      return;
    }

    setIsJoining(true);
    setError(null);

    try {
      const result = await joinSharedList(token);
      setJoinResult(result);

      if (result.success) {
        // Wait a moment to show success, then redirect with list ID
        setTimeout(() => {
          const listId = result.shoppingList?.id;
          onSuccess?.(listId);
        }, 1500);
      } else {
        setError(result.message || t('share.joinError') || 'Fehler beim Beitreten');
      }
    } catch (err) {
      setError(t('share.joinError') || 'Fehler beim Beitreten');
      console.error('Error joining share:', err);
    } finally {
      setIsJoining(false);
    }
  };

  // Format expiration time
  const formatExpiration = (expiresAt) => {
    if (!expiresAt) return null;
    const expires = new Date(expiresAt);
    const now = new Date();
    const diffMs = expires - now;

    if (diffMs <= 0) return t('share.expired') || 'Abgelaufen';

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    }
    return `${diffMinutes}m`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="join-share-container">
        <div className="join-share-card loading">
          <div className="loading-spinner large"></div>
          <p>{t('share.loading') || 'Lade Share-Informationen...'}</p>
        </div>
      </div>
    );
  }

  // Success state
  if (joinResult?.success) {
    return (
      <div className="join-share-container">
        <div className="join-share-card success">
          <div className="success-icon">
            <FaCheck />
          </div>
          <h2>{joinResult.alreadyMember
            ? (t('share.alreadyMember') || 'Du bist bereits Mitglied')
            : (t('share.joinSuccess') || 'Erfolgreich beigetreten!')
          }</h2>
          <p>{t('share.redirecting') || 'Weiterleitung...'}</p>
        </div>
      </div>
    );
  }

  // Error state (invalid share)
  if (error && !shareInfo?.valid) {
    return (
      <div className="join-share-container">
        <div className="join-share-card error">
          <div className="error-icon">
            <FaExclamationTriangle />
          </div>
          <h2>{t('share.invalidTitle') || 'Ungültiger Link'}</h2>
          <p>{error}</p>
          <button className="back-button" onClick={onClose}>
            {t('share.backHome') || 'Zurück zur Startseite'}
          </button>
        </div>
      </div>
    );
  }

  // Valid share - show info and join button
  return (
    <div className="join-share-container">
      <div className="join-share-card">
        <div className="share-icon">
          <FaShoppingCart />
        </div>

        <h2>{t('share.joinTitle') || 'Einkaufszettel beitreten'}</h2>

        <div className="share-details">
          <div className="detail-row">
            <FaListUl className="detail-icon" />
            <span className="detail-label">{t('share.listName') || 'Liste'}:</span>
            <span className="detail-value">{shareInfo?.listName || '-'}</span>
          </div>

          <div className="detail-row">
            <FaUser className="detail-icon" />
            <span className="detail-label">{t('share.owner') || 'Erstellt von'}:</span>
            <span className="detail-value">{shareInfo?.ownerName || '-'}</span>
          </div>

          <div className="detail-row">
            <FaShoppingCart className="detail-icon" />
            <span className="detail-label">{t('share.items') || 'Artikel'}:</span>
            <span className="detail-value">{shareInfo?.itemCount || 0}</span>
          </div>

          {shareInfo?.expiresAt && (
            <div className="detail-row">
              <FaClock className="detail-icon" />
              <span className="detail-label">{t('share.expiresIn') || 'Gültig noch'}:</span>
              <span className="detail-value expires">
                {formatExpiration(shareInfo.expiresAt)}
              </span>
            </div>
          )}
        </div>

        {error && <p className="error-message">{error}</p>}

        <div className="action-buttons">
          <button
            className="join-button"
            onClick={handleJoin}
            disabled={isJoining}
          >
            {isJoining ? (
              <span className="loading-spinner small"></span>
            ) : user ? (
              <>{t('share.joinNow') || 'Jetzt beitreten'}</>
            ) : (
              <>{t('share.loginToJoin') || 'Anmelden & Beitreten'}</>
            )}
          </button>

          <button className="cancel-button" onClick={onClose}>
            {t('share.cancel') || 'Abbrechen'}
          </button>
        </div>

        {!user && (
          <p className="login-hint">
            {t('share.loginRequired') || 'Du musst angemeldet sein, um der Liste beizutreten.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default JoinShareHandler;
